/**
 * Unified Inbox Hooks — Packet 1 + Packet 2
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RorosInboxService } from '@/services/roros/inbox.service';
import { rorosKeys } from './keys';
import type { InboxQueryParams } from '@/types/roros/inbox';

const STALE_TIME = 30_000; // 30s — inbox is action-oriented, refresh frequently
const GC_TIME = 2 * 60_000;

export function useRorosInbox(params?: InboxQueryParams) {
  const paramKey = params
    ? Object.fromEntries(
        Object.entries(params)
          .filter(([, v]) => v !== undefined && v !== null)
          .map(([k, v]) => [k, String(v)]),
      )
    : undefined;

  return useQuery({
    queryKey: rorosKeys.inbox.list(paramKey),
    queryFn: () => RorosInboxService.list(params),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ─── Packet 2: Action mutations ────────────────────────────────────────────

/** Invalidate the full inbox list (all param variants) after any action. */
function useInboxInvalidator() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['roros', 'inbox'] });
}

/**
 * AC-inbox-10: QC transition — Approve (→ released) or Request Changes (→ in_progress).
 * Uses item.sourceId (NOT the prefixed id).
 */
export function useRorosInboxQcTransition() {
  const invalidate = useInboxInvalidator();
  return useMutation({
    mutationFn: ({
      sourceId,
      toStatus,
      reason,
    }: {
      sourceId: string;
      toStatus: 'released' | 'in_progress';
      reason?: string;
    }) => RorosInboxService.qcTransition(sourceId, { toStatus, reason }),
    onSuccess: () => invalidate(),
  });
}

/**
 * AC-inbox-11: Decision approve.
 * Uses item.sourceId (NOT the prefixed id).
 */
export function useRorosInboxDecisionApprove() {
  const invalidate = useInboxInvalidator();
  return useMutation({
    mutationFn: ({ sourceId }: { sourceId: string }) =>
      RorosInboxService.decisionApprove(sourceId),
    onSuccess: () => invalidate(),
  });
}

/**
 * AC-inbox-11: Decision reject (requires reason).
 * Uses item.sourceId (NOT the prefixed id).
 */
export function useRorosInboxDecisionReject() {
  const invalidate = useInboxInvalidator();
  return useMutation({
    mutationFn: ({ sourceId, reason }: { sourceId: string; reason: string }) =>
      RorosInboxService.decisionReject(sourceId, reason),
    onSuccess: () => invalidate(),
  });
}

/**
 * AC-inbox-12: Cascade approve.
 * Uses item.sourceId (NOT the prefixed id).
 */
export function useRorosInboxCascadeApprove() {
  const invalidate = useInboxInvalidator();
  return useMutation({
    mutationFn: ({ sourceId }: { sourceId: string }) =>
      RorosInboxService.cascadeApprove(sourceId),
    onSuccess: () => invalidate(),
  });
}

/**
 * AC-inbox-12: Cascade reject (requires reason).
 * Uses item.sourceId (NOT the prefixed id).
 */
export function useRorosInboxCascadeReject() {
  const invalidate = useInboxInvalidator();
  return useMutation({
    mutationFn: ({ sourceId, reason }: { sourceId: string; reason: string }) =>
      RorosInboxService.cascadeReject(sourceId, reason),
    onSuccess: () => invalidate(),
  });
}

/**
 * AC-inbox-12: Cascade request ad-hoc review.
 * Uses item.sourceId (NOT the prefixed id).
 */
export function useRorosInboxCascadeRequestAdHoc() {
  const invalidate = useInboxInvalidator();
  return useMutation({
    mutationFn: ({ sourceId }: { sourceId: string }) =>
      RorosInboxService.cascadeRequestAdHoc(sourceId),
    onSuccess: () => invalidate(),
  });
}
